import { api } from '@/lib/api/client';
import type {
  Contract,
  ContractFilters,
  ContractListResponse,
  CreateContractDto,
  UpdateContractDto,
  CreateBillingPeriodDto,
  BillingPeriod,
  CurrencyRate,
  RecalculationSummary,
} from '../types';

const CONTRACTS_URL = '/contracts';

export const contractsApi = {
  // Get all contracts with filters
  getAll: async (filters?: ContractFilters): Promise<ContractListResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    return api.get<ContractListResponse>(CONTRACTS_URL, { params });
  },

  // Get single contract by ID
  getById: async (id: number | string): Promise<Contract> => {
    return api.get<Contract>(`${CONTRACTS_URL}/${id}`);
  },

  // Create new contract
  create: async (data: CreateContractDto): Promise<Contract> => {
    return api.post<Contract>(CONTRACTS_URL, data);
  },

  // Update contract
  update: async ({ id, data }: { id: number | string; data: UpdateContractDto }): Promise<Contract> => {
    return api.patch<Contract>(`${CONTRACTS_URL}/${id}`, data);
  },

  // Delete contract
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${CONTRACTS_URL}/${id}`);
  },

  // Change contract status
  changeStatus: async ({ url, id }: { url: string; id: number | string }): Promise<Contract> => {
    return api.patch<Contract>(`${CONTRACTS_URL}/${url}/${id}`, {});
  },

  // Activate contract
  activate: async (id: number | string): Promise<Contract> => {
    return api.patch<Contract>(`${CONTRACTS_URL}/activate/${id}`, {});
  },

  // Close contract
  close: async (id: number | string): Promise<Contract> => {
    return api.patch<Contract>(`${CONTRACTS_URL}/close/${id}`, {});
  },

  // Renew contract (set to NEW status)
  renew: async (id: number | string): Promise<Contract> => {
    return api.patch<Contract>(`${CONTRACTS_URL}/renew/${id}`, {});
  },

  // Re-price every task of an HOURLY contract from scratch.
  // Non-destructive: task prices + period durations only — balance, bills and
  // payments are left untouched.
  recalculate: async (id: number | string): Promise<RecalculationSummary> => {
    return api.post<RecalculationSummary>(`${CONTRACTS_URL}/recalculate/${id}`, {});
  },

  // Start billing period
  startBillingPeriod: async (data: CreateBillingPeriodDto): Promise<BillingPeriod> => {
    return api.post<BillingPeriod>('/billing-periods/start', data);
  },

  // Stop billing period (contractId, not billingPeriodId)
  stopBillingPeriod: async (contractId: number | string): Promise<BillingPeriod> => {
    return api.post<BillingPeriod>(`/billing-periods/stop/${contractId}`, {});
  },

  // Get current currency rate
  getCurrencyRate: async (): Promise<CurrencyRate> => {
    return api.get<CurrencyRate>('/currency/usd-uzs');
  },
};

// Query keys for React Query
export const contractsQueryKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractsQueryKeys.all, 'list'] as const,
  list: (filters?: ContractFilters) => [...contractsQueryKeys.lists(), filters] as const,
  details: () => [...contractsQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...contractsQueryKeys.details(), id] as const,
};
